import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PartnerService, DonorCareReportData } from 'src/app/api/partner.service';
import { UiModule } from 'src/app/ui/ui.module';
import { DateRangePickerComponent } from 'src/app/ui/date-range-picker/date-range-picker.component';
import { SavedSearchComponent } from 'src/app/ui/saved-search/saved-search.component';
import { IconService } from 'src/app/ui/icon.service';
import { DateTime } from 'luxon';
import { TableHeadersComponent, TableColumnsComponent, Column, TableCaptionComponent } from 'src/app/ui/tables';
import { LinkableLocationsService, ProfileLocation } from 'src/app/api/linkable-locations.service';

import { DateRangePresets } from 'src/app/ui/date-range-picker/date-range-picker.component';
import { ActivatedRoute, Router } from '@angular/router';

import { LayoutService } from '../../mission-control/services/layout.service';

@Component({
  selector: 'app-donor-care-report',
  standalone: true,
  imports: [
    CommonModule,
    UiModule,
    DateRangePickerComponent,
    SavedSearchComponent,
    TableCaptionComponent,
    TableHeadersComponent,
    TableColumnsComponent
  ],
  templateUrl: './donor-care-report.component.html',
  styleUrl: './donor-care-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DonorCareReportComponent {
  loading = signal(false);
  reportData = signal<DonorCareReportData[]>([]);
  searchPerformed = signal(false);
  locations = signal<ProfileLocation[]>([]);

  columns = signal<Column<DonorCareReportData>[]>([
    { field: 'locationName', header: 'Location Name', type: 'text', sortable: true, filterable: true },
    { field: 'partnerName', header: 'Partner Name', type: 'text', sortable: true, filterable: true },
    { field: 'trimester', header: 'Trimester', type: 'number', sortable: true, filterable: true },
    { field: 'year', header: 'Year', type: 'number', sortable: true, filterable: true },
    { field: 'updateCount', header: 'Update Count', type: 'number', sortable: true, filterable: true }
  ]);

  searchForm: FormGroup;
  dateRange = [DateTime.now().startOf('year').toJSDate(), DateTime.now().endOf('year').toJSDate()];

  trimesterPresets: DateRangePresets[] = [
    {
      label: 'This Year',
      value: [
        DateTime.now().startOf('year').toJSDate(),
        DateTime.now().endOf('year').toJSDate(),
      ],
    },
    {
      label: 'T1',
      value: [
        DateTime.now().startOf('year').toJSDate(),
        DateTime.now().set({ month: 4 }).endOf('month').toJSDate(),
      ],
    },
    {
      label: 'T2',
      value: [
        DateTime.now().set({ month: 5 }).startOf('month').toJSDate(),
        DateTime.now().set({ month: 8 }).endOf('month').toJSDate(),
      ],
    },
    {
      label: 'T3',
      value: [
        DateTime.now().set({ month: 9 }).startOf('month').toJSDate(),
        DateTime.now().set({ month: 12 }).endOf('month').toJSDate(),
      ],
    }
  ];

  constructor(
    public icons: IconService,
    private partnerService: PartnerService,
    private locationService: LinkableLocationsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    public layoutService: LayoutService
  ) {
    this.searchForm = this.fb.group({
      dateRange: [this.dateRange, Validators.required],
      locationIds: [[]]
    });
  }

  ngOnInit() {
    this.loadLocations();
    this.route.queryParamMap.subscribe(params => {
      const startDate = params.get('startDate');
      const endDate = params.get('endDate');
      const locationIds = params.getAll('locationIds');
      let changed = false;
      if (startDate && endDate) {
        this.searchForm.patchValue({
          dateRange: [DateTime.fromFormat(startDate, "yyyy-MM-dd", {zone: 'local'}).toJSDate(), DateTime.fromFormat(endDate, "yyyy-MM-dd", {zone: 'local'}).toJSDate()]
        });
        changed = true;
      }
      if (locationIds && locationIds.length > 0) {
        this.searchForm.patchValue({ locationIds });
        changed = true;
      }
      if (changed) {
        this.loadData();
      }
    });
  }

  loadLocations() {
    this.locationService.getLocations('active-public').subscribe({
      next: (locations) => {
        this.locations.set(locations);
      },
      error: (error) => {
        console.error('Error loading locations:', error);
      }
    });
  }

  loadData() {
    const formValue = this.searchForm.value;
    let [start, end] = formValue.dateRange;
    const locationIds = formValue.locationIds && formValue.locationIds.length > 0
      ? formValue.locationIds.map((loc: any) => typeof loc === 'string' ? loc : loc.id)
      : undefined;
    const startStr = DateTime.fromJSDate(start).toFormat('yyyy-MM-dd');
    const endStr = DateTime.fromJSDate(end).toFormat('yyyy-MM-dd');

    console.log('[donor-care-report] request inputs', {
      startDate: startStr,
      endDate: endStr,
      locationIdsCount: locationIds?.length ?? 0,
      locationIds,
    });

    this.loading.set(true);
    this.searchPerformed.set(true);
    this.partnerService.getDonorCareReportData(startStr, endStr, locationIds).subscribe({
      next: (data) => {
        this.reportData.set(data);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading donor care report data:', error);
        this.loading.set(false);
      }
    });
  }

  onSearch() {
    this.loadData();
  }
} 